import { makeStyles, Menu, MenuGroup, MenuGroupHeader, MenuList, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import { useEffect, useMemo, useRef, useState } from "react";
import Status from "../../../enums/Status";
import { useTranslation } from "react-i18next";
import DesktopMegaArea from "./DesktopMegaArea";
import MenuHeader from "./MenuHeader";
import MenuItemLink from "./MenuItemLink";
import MenuLink from "./MenuLink";
import { dolphinTokens } from "../../../utils/Theme";
import INavigationArea from "../../../models/navigation/INavigationArea";
import INavigationSection from "../../../models/navigation/INavigationSection";

const useStyles = makeStyles({
    menu: {
        display: "flex",
		alignItems: "center",
		gap: "1rem",
		flexShrink: 0,
		overflow: "visible",
		
		"@media (max-width: 768px)": {
			display: "none",
		}
    },
    popoverSheet: {
        position: "relative",
        overflow: "hidden",
        padding: "32px 24px 76px",
        border: "none",
        borderRadius: "22px",
        backgroundColor: dolphinTokens.colorNeutralBackground1,
        boxShadow: "0 32px 80px rgba(0, 25, 35, 0.4)",
    },
    band: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "72px",
        color: dolphinTokens.colorLightBlue,
    },
    wave: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
    },
    storyMenuList: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: "56px",
        padding: 0,
    },
    group: {
        padding: 0,
    },
    header: {
        color: dolphinTokens.colorLightBlue,
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        marginTop: 0,
        marginBottom: "6px",
    }
});

const FREE_ASSESSMENT_PATH = "/services";

interface IDesktopMenuProps {
    areas: INavigationArea[];
    status: Status;
}

export default function DesktopMenu(props: IDesktopMenuProps) {
    const { areas: areas, status } = props;

    const { t } = useTranslation("navigation");
    const styles = useStyles();

    const [storyMenuOpen, setStoryMenuOpen] = useState(false);
    const storyMenuCloseTimer = useRef<number | undefined>(undefined);

    useEffect(() => {
        return () => window.clearTimeout(storyMenuCloseTimer.current);
    }, []);

    const openStoryMenu = () => {
        window.clearTimeout(storyMenuCloseTimer.current);
        setStoryMenuOpen(true);
    };

    const closeStoryMenuSoon = () => {
        window.clearTimeout(storyMenuCloseTimer.current);
        storyMenuCloseTimer.current = window.setTimeout(() => setStoryMenuOpen(false), 160);
    };

    const isSupportSite = (process.env.REACT_APP_IS_SUPPORT_SITE || "").toLowerCase() === "true";

    const allLink = { to: "/services", label: t("mega.everything") };
    const promo = { tag: t("mega.promoTag"), label: t("mega.promoTitle"), to: FREE_ASSESSMENT_PATH };

    const solutionsArea = useMemo<INavigationArea | null>(() => {
        if (areas.length === 0) {
            return null;
        }

        const rail = [...areas]
            .sort((a, b) => a.order - b.order)
            .map((area, index) => {
                const links = [...area.sections]
                    .sort((a, b) => a.order - b.order)
                    .flatMap(section => section.links)
                    .filter(link => !link.isHidden)
                    .map(link => ({ ...link, path: `${area.slug}/${link.path}` }));

                return {
                    id: area.id,
                    name: area.name,
                    order: index,
                    links,
                    groups: links.length === 0 ? [] : undefined,
                };
            }) as unknown as INavigationSection[];

        return { ...areas[0], name: t("Solutions"), slug: "", sections: rail };
    }, [areas, t]);

    return (
        <div className={styles.menu}>
            <MenuLink to="/" label={t("home")} />
            <Menu 
                positioning={{ autoSize: true, align: "center", offset: { mainAxis: 9 }, overflowBoundaryPadding: 16 }} 
                open={storyMenuOpen}
                onOpenChange={(e, data) => setStoryMenuOpen(data.open)}
            >
                <MenuTrigger disableButtonEnhancement>
                    <div
                        onMouseEnter={openStoryMenu}
                        onMouseLeave={closeStoryMenuSoon}
                    >
                        <MenuHeader label={t("ourStory.ourStory")} />
                    </div>
                </MenuTrigger>
                <MenuPopover
                    className={styles.popoverSheet}
                    onMouseEnter={openStoryMenu}
                    onMouseLeave={closeStoryMenuSoon}
                >
                    <MenuList className={styles.storyMenuList}>
                        <MenuGroup className={styles.group}>
                            <MenuGroupHeader className={styles.header}>
                                {t("ourStory.about")}
                            </MenuGroupHeader>

                            <MenuItemLink to="/ourstory" label={t("ourStory.ourStory")} />
                            <MenuItemLink to="/partners" label={t("ourStory.partners")} />
                            <MenuItemLink to="/jobs" label={t("ourStory.jobs")} />
                            <MenuItemLink to="/sustainability" label={t("ourStory.sustainability")} />
                        </MenuGroup>
                        <MenuGroup className={styles.group}>
                            <MenuGroupHeader className={styles.header}>
                                {t("ourStory.portfolio")}
                            </MenuGroupHeader>

                            <MenuItemLink to="/casestudies" label={t("ourStory.caseStudies")} />
                            <MenuItemLink to="/testimonials" label={t("ourStory.testimonials")} />
                            <MenuItemLink to="/awards" label={t("ourStory.awards")} />
                        </MenuGroup>
                    </MenuList>
                    <div className={styles.band} aria-hidden="true">
                        <svg className={styles.wave} viewBox="0 0 800 116" preserveAspectRatio="none" focusable="false">
                            <path fill="currentColor" opacity="0.38" d="M0 40C130 8 260 10 400 36S660 72 800 32V116H0Z" />
                            <path fill="currentColor" d="M0 60C140 32 290 36 420 60S680 88 800 54V116H0Z" />
                        </svg>
                    </div>
                </MenuPopover>
            </Menu>
            {
                solutionsArea && (
                    <DesktopMegaArea area={solutionsArea} allLink={allLink} promo={promo} />
                )
            }
            <MenuLink to="/blog" label={t("blog")} />
            
            {isSupportSite && (
                <>
                    <MenuLink to="/contact" label={t("contact")} />
                    <MenuLink to="https://app.dolphin-support.co.uk/" label={t("login")} primary={true} />
                </>
            )}

            {!isSupportSite && (
                <MenuLink to="/contact" label={t("contact")} primary={true} />
            )}

            
        </div>
    );
}